
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
// import { FileText, Calendar, ExternalLink, TrendingUp, TrendingDown } from 'lucide-react'
import { getReports } from '../services/api'
import { FileText, Calendar, ExternalLink } from 'lucide-react'
import type { Report } from '../../../shared/types'

export default function ReportsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: () => getReports({ page: 1, limit: 20, sort: 'createdAt' }),
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="animate-pulse space-y-3">
              <div className="h-5 bg-gray-200 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      <Head>
        <title>Reports - Accessibility Checker</title>
        <meta name="description" content="View your accessibility scan reports and history" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={getCanonicalUrl('/reports')} />
      </Head>
      <div className="space-y-6">
        <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Accessibility Reports</h1>
        <p className="text-gray-600">View and manage all your accessibility test reports</p>
      </div>

      {data?.reports && data.reports.length > 0 ? (
        <div className="space-y-4">
          {data.reports.map((report: Report) => {
            const scoreColor = report.summary.score >= 90 
              ? 'text-green-600' 
              : report.summary.score >= 70 
              ? 'text-yellow-600' 
              : 'text-red-600'

            return (
              <Link
                key={report.id}
                to={`/reports/${report.id}`}
                className="block bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {report.metadata.pageTitle || 'Untitled Page'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">{report.url}</p>
                    
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {new Date(report.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Score:</span>
                        <span className={`font-semibold ${scoreColor}`}>
                          {report.summary.score}/100
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-red-600">
                          {report.summary.errors} errors
                        </span>
                        <span className="text-yellow-600">
                          {report.summary.warnings} warnings
                        </span>
                      </div>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400" />
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Reports Yet
          </h3>
          <p className="text-gray-600 mb-4">
            Start testing pages to see accessibility reports here
          </p>
          <Link
            to="/scan"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Start Testing
          </Link>
        </div>
      )}
    </div>
    </>
  )
}
